/**
 * Module registry — single source of truth for the app shell.
 *
 * Each role gets an ordered list of GROUPS (collapsible sections in the sidebar).
 * Each group has ITEMS (modules). An item declares:
 *   key    unique id (used for tab identity + persistence)
 *   label  sidebar / tab caption
 *   icon   phosphor icon component
 *   path   route opened when clicked
 *   match  optional list of path prefixes that belong to this module
 *          (detail/edit routes keep the same tab open instead of spawning a new one)
 *
 * `resolveModule(pathname, role)` maps any route back to its module so the tab
 * strip can keep a single tab per module (OneInvoice behaviour: "Sale Invoice (AR)"
 * stays one tab whether you are on the list, a detail, or the editor).
 */
import {
  ChatsCircle,
  House, Buildings, Users, UserCircleGear, ChartBar, HardDrives, Envelope, Train,
  Chalkboard, Books, Student, GearSix, Question, Exam, Certificate, GraduationCap,
  Funnel, IdentificationBadge, ShieldCheck, Plus, PencilSimple, Eye, TreeStructure, Globe, UsersThree,
  Archive, Notebook, CalendarCheck,
} from '@phosphor-icons/react';

const dashboard = (path) => ({
  key: 'dashboard', label: 'Dashboard', icon: House, path, pinned: true,
});

// Record-level tabs for course screens (shared by instructor + tenant admin)
const COURSE_SUB_TABS = [
  { pattern: /^\/instructor\/courses\/new$/, key: () => 'course:new', label: () => 'New course', icon: Plus },
  { pattern: /^\/instructor\/courses\/([^/]+)\/edit$/, key: (m) => `course:edit:${m[1]}`, label: () => 'Edit course', icon: PencilSimple },
  { pattern: /^\/instructor\/courses\/([^/]+)\/structure$/, key: (m) => `course:structure:${m[1]}`, label: () => 'Course structure', icon: TreeStructure },
  { pattern: /^\/instructor\/courses\/([^/]+)\/nodes\/[^/]+$/, key: (m) => `course:structure:${m[1]}`, label: () => 'Course structure', icon: TreeStructure },
  { pattern: /^\/instructor\/courses\/([^/]+)\/students\/([^/]+)\/analytics$/, key: (m) => `course:view:${m[1]}`, label: () => 'View course', icon: Eye },
  { pattern: /^\/instructor\/courses\/([^/]+)$/, key: (m) => `course:view:${m[1]}`, label: () => 'View course', icon: Eye },
  { pattern: /^\/learn\/course\/([^/]+)$/, key: (m) => `course:preview:${m[1]}`, label: () => 'Preview', icon: Eye },
];

const USER_SUB_TABS = [
  { pattern: /^\/(admin|super-admin)\/users\/new$/, key: () => 'user:new', label: () => 'New user', icon: Plus },
  { pattern: /^\/(admin|super-admin)\/users\/([^/]+)$/, key: (m) => `user:view:${m[2]}`, label: () => 'User', icon: Eye },
];

const QUESTION_SUB_TABS = [
  { pattern: /^\/instructor\/questions\/new$/, key: () => 'question:new', label: () => 'New question', icon: Plus },
  { pattern: /^\/instructor\/questions\/([^/]+)\/edit$/, key: (m) => `question:edit:${m[1]}`, label: () => 'Edit question', icon: PencilSimple },
];

const LEAD_SUB_TABS = [
  { pattern: /^\/crm\/leads\/new$/, key: () => 'lead:new', label: () => 'New lead', icon: Plus },
  { pattern: /^\/crm\/leads\/([^/]+)$/, key: (m) => `lead:view:${m[1]}`, label: () => 'Lead', icon: Eye },
];

const BATCH_SUB_TABS = [
  { pattern: /^\/(admin|instructor)\/batches\/([^/]+)$/, key: (m) => `batch:view:${m[2]}`, label: () => 'Batch', icon: UsersThree },
];

const EXAM_SUB_TABS = [
  { pattern: /^\/instructor\/exams\/new$/, key: () => 'exam:new', label: () => 'New exam', icon: Plus },
  { pattern: /^\/instructor\/exams\/([^/]+)\/report$/, key: (m) => `exam:report:${m[1]}`, label: () => 'Exam report', icon: Eye },
  { pattern: /^\/instructor\/exams\/([^/]+)\/preview$/, key: (m) => `exam:preview:${m[1]}`, label: () => 'Preview exam', icon: Eye },
  { pattern: /^\/instructor\/exams\/([^/]+)$/, key: (m) => `exam:edit:${m[1]}`, label: () => 'Edit exam', icon: PencilSimple },
];

const LEARNER_SUB_TABS = [
  { pattern: /^\/(?:learn|preview)\/course\/([^/]+)$/, key: (m) => `learn:course:${m[1]}`, label: () => 'Course', icon: Books },
  { pattern: /^\/(?:learn|preview)\/exams\/([^/]+)$/, key: (m) => `learn:exam:${m[1]}`, label: () => 'Exam', icon: Exam },
  { pattern: /^\/learn\/exams\/([^/]+)\/results$/, key: (m) => `learn:exam-results:${m[1]}`, label: () => 'Results', icon: Exam },
];

export const MODULE_GROUPS = {
  super_admin: [
    {
      key: 'overview', label: 'Overview', icon: House,
      items: [dashboard('/super-admin')],
    },
    {
      key: 'platform', label: 'Platform', icon: Buildings,
      items: [
        { key: 'tenants', label: 'Tenants', icon: Buildings, path: '/super-admin/tenants' },
        { key: 'users', label: 'Users', icon: Users, path: '/super-admin/users', subTabs: USER_SUB_TABS },
      ],
    },
    {
      key: 'crm', label: 'CRM', icon: Funnel,
      items: [
        { key: 'crm-overview', label: 'CRM Overview', icon: ChartBar, path: '/super-admin/crm' },
        { key: 'crm-leads', label: 'Leads', icon: Funnel, path: '/crm', subTabs: LEAD_SUB_TABS },
      ],
    },
    {
      key: 'administration', label: 'Administration', icon: GearSix,
      items: [
        { key: 'domains', label: 'Platform domains', icon: Globe, path: '/super-admin/domains' },
        { key: 'storage', label: 'Storage', icon: HardDrives, path: '/super-admin/storage' },
        { key: 'smtp', label: 'Email (SMTP)', icon: Envelope, path: '/super-admin/smtp' },
        { key: 'railway', label: 'Railway Domains', icon: Train, path: '/super-admin/railway' },
        { key: 'backup', label: 'Backup & restore', icon: Archive, path: '/super-admin/backup' },
      ],
    },
  ],

  tenant_admin: [
    {
      key: 'overview', label: 'Overview', icon: House,
      items: [
        dashboard('/admin'),
        { key: 'reports', label: 'Reports', icon: ChartBar, path: '/admin/reports' },
      ],
    },
    {
      key: 'people', label: 'People', icon: Users,
      items: [
        { key: 'users', label: 'Users', icon: Users, path: '/admin/users', subTabs: USER_SUB_TABS },
        { key: 'batches', label: 'Batches', icon: UsersThree, path: '/admin/batches', subTabs: BATCH_SUB_TABS },
        { key: 'community', label: 'Community & doubts', icon: ChatsCircle, path: '/admin/community' },
        { key: 'diary', label: 'Student diary', icon: Notebook, path: '/diary' },
        { key: 'attendance', label: 'Attendance', icon: CalendarCheck, path: '/attendance' },
        { key: 'instructors', label: 'Instructors', icon: Chalkboard, path: '/admin/instructors' },
      ],
    },
    {
      key: 'learning', label: 'Learning', icon: Books,
      items: [
        { key: 'courses', label: 'Courses', icon: Books, path: '/admin/courses', match: ['/instructor/courses', '/learn/course'], subTabs: COURSE_SUB_TABS },
        { key: 'questions', label: 'Question Bank', icon: Question, path: '/instructor/questions', subTabs: QUESTION_SUB_TABS },
        { key: 'exams', label: 'Exams', icon: Exam, path: '/instructor/exams', subTabs: EXAM_SUB_TABS },
      ],
    },
    {
      key: 'crm', label: 'CRM', icon: Funnel,
      items: [
        { key: 'crm-leads', label: 'Leads & Admissions', icon: Funnel, path: '/crm', match: ['/admin/crm'], subTabs: LEAD_SUB_TABS },
      ],
    },
    {
      key: 'administration', label: 'Administration', icon: GearSix,
      items: [
        { key: 'roles', label: 'Roles', icon: IdentificationBadge, path: '/admin/roles' },
        { key: 'settings', label: 'Organization Settings', icon: GearSix, path: '/admin/settings' },
      ],
    },
  ],

  instructor: [
    {
      key: 'overview', label: 'Overview', icon: House,
      items: [dashboard('/instructor')],
    },
    {
      key: 'teaching', label: 'Teaching', icon: Chalkboard,
      items: [
        { key: 'courses', label: 'My Courses', icon: Books, path: '/instructor/courses', match: ['/learn/course'], subTabs: COURSE_SUB_TABS },
        { key: 'students', label: 'My Students', icon: Student, path: '/instructor/students', match: ['/admin/users'], subTabs: USER_SUB_TABS },
        { key: 'batches', label: 'Batches', icon: UsersThree, path: '/instructor/batches', subTabs: BATCH_SUB_TABS },
        { key: 'community', label: 'Community & doubts', icon: ChatsCircle, path: '/instructor/community' },
        { key: 'diary', label: 'Student diary', icon: Notebook, path: '/diary' },
        { key: 'attendance', label: 'Attendance', icon: CalendarCheck, path: '/attendance' },
      ],
    },
    {
      key: 'assessment', label: 'Assessment', icon: Exam,
      items: [
        { key: 'questions', label: 'Question Bank', icon: Question, path: '/instructor/questions', subTabs: QUESTION_SUB_TABS },
        { key: 'exams', label: 'Exams', icon: Exam, path: '/instructor/exams', subTabs: EXAM_SUB_TABS },
      ],
    },
    {
      key: 'crm', label: 'CRM', icon: Funnel,
      items: [
        { key: 'crm-leads', label: 'My Leads', icon: Funnel, path: '/crm', subTabs: LEAD_SUB_TABS },
      ],
    },
  ],

  parent: [
    {
      key: 'overview', label: 'My children', icon: UsersThree,
      items: [
        { key: 'diary', label: 'Student diary', icon: Notebook, path: '/diary', pinned: true },
        { key: 'attendance', label: 'Attendance', icon: CalendarCheck, path: '/attendance/student' },
      ],
    },
  ],

  learner: [
    {
      key: 'overview', label: 'Overview', icon: House,
      items: [dashboard('/learn')],
    },
    {
      key: 'learning', label: 'Learning', icon: GraduationCap,
      items: [
        { key: 'my-courses', label: 'My Learning', icon: Books, path: '/learn/my-courses', match: ['/learn/course', '/learn/exams', '/preview/course', '/preview/exams'], subTabs: LEARNER_SUB_TABS },
        { key: 'catalog', label: 'Course Catalog', icon: GraduationCap, path: '/learn/catalog' },
        { key: 'diary', label: 'My diary', icon: Notebook, path: '/learn/diary' },
        { key: 'attendance', label: 'My attendance', icon: CalendarCheck, path: '/learn/attendance' },
      ],
    },
    {
      key: 'achievements', label: 'Achievements', icon: Certificate,
      items: [
        { key: 'certificates', label: 'Certificates', icon: Certificate, path: '/learn/certificates' },
      ],
    },
  ],
};

// Routes that exist for every role but are not a sidebar module.
const COMMON_MODULES = [
  { key: 'profile', label: 'My Profile', icon: IdentificationBadge, path: '/profile' },
];

/**
 * The sidebar for a role — narrowed to `allowedModules` when the tenant has given this
 * person a role of its own (a tutor with only the diary and attendance, say).
 *
 * `null` means no narrowing: the plain built-in role. An empty group is dropped rather
 * than left as a heading with nothing under it. The server enforces the same list, so
 * this is about not offering what would be refused, never about security.
 */
export function getGroups(role, allowedModules = null) {
  const groups = MODULE_GROUPS[role] || [];
  if (!allowedModules || !allowedModules.length) return groups;
  const allowed = new Set(allowedModules);
  return groups
    .map((g) => ({ ...g, items: g.items.filter((i) => allowed.has(i.key)) }))
    .filter((g) => g.items.length > 0);
}

export function getAllModules(role) {
  const items = getGroups(role).flatMap((g) => g.items);
  return [...items, ...COMMON_MODULES];
}

function pathMatches(pathname, base) {
  return pathname === base || pathname.startsWith(base + '/');
}

/**
 * Find the module a pathname belongs to. Longest matching prefix wins, so
 * `/instructor/courses/123/structure` resolves to "courses", not "dashboard".
 */
export function resolveModule(pathname, role) {
  const modules = getAllModules(role);
  let best = null;
  let bestLen = -1;
  for (const m of modules) {
    const candidates = [m.path, ...(m.match || [])];
    for (const c of candidates) {
      if (pathMatches(pathname, c) && c.length > bestLen) {
        best = m;
        bestLen = c.length;
      }
    }
  }
  return best;
}

/**
 * Resolve a pathname to the tab it should open:
 *   { key, label, icon, path, parentKey, pinned }
 * Record routes declared in a module's subTabs get their own tab (child of the module);
 * everything else maps to the module tab itself.
 */
export function resolveTab(pathname, role) {
  const module = resolveModule(pathname, role);
  if (!module) return null;
  for (const rule of module.subTabs || []) {
    const m = pathname.match(rule.pattern);
    if (m) {
      return {
        key: rule.key(m),
        label: rule.label(m),
        icon: rule.icon || module.icon,
        parentKey: module.key,
        pinned: false,
        isRecord: true,
      };
    }
  }
  return { key: module.key, label: module.label, icon: module.icon, parentKey: null, pinned: !!module.pinned, isRecord: false };
}

export function getHomePath(role) {
  return {
    super_admin: '/super-admin',
    tenant_admin: '/admin',
    instructor: '/instructor',
    parent: '/diary',
    learner: '/learn',
  }[role] || '/';
}

export const ROLE_LABELS = {
  super_admin: 'Super Admin',
  tenant_admin: 'Tenant Admin',
  instructor: 'Instructor',
  learner: 'Learner',
  parent: 'Parent',
};

export const ROLE_ICONS = {
  super_admin: ShieldCheck,
  tenant_admin: UserCircleGear,
  instructor: Chalkboard,
  learner: Student,
};
